import { ArrowLeft, ChevronRight, MapPin, Star, Zap } from "lucide-react";
import { PhoneMock } from "../PhoneMock";

/**
 * App preview: the real Whistll Venue Detail screen.
 *
 * Mirrors apps/mobile/src/pages/VenueDetail.jsx: minimal back link,
 * photo hero with rating pill, amenity chips, then an "Available Courts (N)"
 * list of pitches with format + surface badges and per-person pricing.
 *
 * Shown on the /venues marketing page so partners see exactly how their
 * venue renders to players inside the app.
 */
export function VenueInAppMock({
  className,
  venueName = "Brixton Rec",
  address = "Brixton Station Rd, London",
  rating = 4.8,
  amenities = ["Parking", "Showers", "Indoor", "4 courts"],
}: {
  className?: string;
  venueName?: string;
  address?: string;
  rating?: number;
  amenities?: string[];
}) {
  return (
    <PhoneMock className={className}>
      {/* Status bar */}
      <div className="flex items-center justify-between px-6 pt-4 pb-1 text-[10px] font-semibold">
        <span>9:41</span>
        <span className="flex items-center gap-1 text-[9px]">● ● ●</span>
      </div>

      {/* Back link */}
      <div className="px-4 pt-3 pb-2">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#8899A6]">
          <ArrowLeft className="h-3.5 w-3.5" />
          Venues
        </div>
      </div>

      {/* Scrollable content */}
      <div className="px-4">
        {/* Hero card: image + info block */}
        <div className="overflow-hidden rounded-2xl border border-[#1e2832]">
          {/* Photo (stand-in: dark gradient with subtle court lines) */}
          <div
            className="relative aspect-[16/9] w-full"
            style={{
              backgroundImage:
                "linear-gradient(135deg, #1a2a3a 0%, #0d1117 40%, #1e2832 100%)",
            }}
          >
            <svg
              className="absolute inset-0 h-full w-full opacity-25"
              viewBox="0 0 280 160"
              preserveAspectRatio="none"
              aria-hidden
            >
              <rect
                x="30"
                y="20"
                width="220"
                height="120"
                fill="none"
                stroke="#FF8C42"
                strokeWidth="1.5"
              />
              <circle
                cx="140"
                cy="80"
                r="24"
                fill="none"
                stroke="#FF8C42"
                strokeWidth="1.5"
              />
              <line
                x1="140"
                y1="20"
                x2="140"
                y2="140"
                stroke="#FF8C42"
                strokeWidth="1.5"
              />
            </svg>
          </div>

          {/* Info block below photo */}
          <div className="bg-[#131a20] p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h1 className="text-[15px] font-black leading-tight text-[#F5F5DC]">
                  {venueName}
                </h1>
                <div className="mt-1 flex items-center gap-1">
                  <MapPin className="h-2.5 w-2.5 shrink-0 text-[#4a5568]" />
                  <span className="truncate text-[10px] text-[#8899A6]">
                    {address}
                  </span>
                </div>
              </div>
              {/* Rating pill */}
              <div className="flex shrink-0 items-center gap-1 rounded-full bg-[#FF6200]/10 px-2 py-1 text-[10px] font-bold text-[#FF6200]">
                <Star className="h-2.5 w-2.5 fill-current" />
                {rating}
              </div>
            </div>

            {/* Amenity pills */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {amenities.map((a) => (
                <span
                  key={a}
                  className="rounded-full bg-[#FF6200]/10 px-2 py-0.5 text-[9px] font-semibold text-[#FF6200]"
                >
                  {a}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Available Courts heading */}
        <h2 className="mb-2 mt-4 text-[9px] font-bold uppercase tracking-widest text-[#4a5568]">
          Available Courts (2)
        </h2>

        {/* Pitch rows */}
        <div className="flex flex-col gap-2">
          <PitchRow
            name="Main Hall"
            format="5v5"
            surface="Indoor"
            surfaceColor={{ bg: "rgba(245,158,11,0.1)", color: "#f59e0b" }}
            price="£8.50"
          />
          <PitchRow
            name="Court 2"
            format="3v3"
            surface="3G"
            surfaceColor={{ bg: "rgba(255,98,0,0.1)", color: "#FF6200" }}
            price="£5.00"
          />
        </div>
      </div>
    </PhoneMock>
  );
}

function PitchRow({
  name,
  format,
  surface,
  surfaceColor,
  price,
}: {
  name: string;
  format: string;
  surface: string;
  surfaceColor: { bg: string; color: string };
  price: string;
}) {
  return (
    <div className="flex items-center justify-between rounded-2xl border border-[#1e2832] bg-[#131a20] p-3">
      <div className="min-w-0">
        <p className="text-[11px] font-bold text-[#F5F5DC]">{name}</p>
        <div className="mt-1 flex items-center gap-1.5">
          <span className="rounded-full bg-[#FF6200]/10 px-2 py-0.5 text-[9px] font-bold text-[#FF6200]">
            {format}
          </span>
          <span
            className="rounded-full px-2 py-0.5 text-[9px] font-bold"
            style={{ background: surfaceColor.bg, color: surfaceColor.color }}
          >
            {surface}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="text-right">
          <div className="flex items-center gap-0.5 text-[#FF6200]">
            <Zap className="h-3 w-3" />
            <span className="text-[12px] font-black">{price}</span>
          </div>
          <span className="text-[8px] text-[#4a5568]">per person</span>
        </div>
        <ChevronRight className="h-3 w-3 text-[#4a5568]" />
      </div>
    </div>
  );
}
